import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

export const exportToExcel = (data, filename = 'Countries') => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Countries');
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  } catch (error) {
    console.error('Excel export error:', error);
    throw new Error('Failed to generate Excel: ' + error.message);
  }
};

export const exportToCSV = (data, filename = 'Countries') => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }
    
    const headers = Object.keys(data[0]);
    const csvRows = [];
    
    csvRows.push(headers.join(','));
  
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        if (value === null || value === undefined) return '';
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      });
      csvRows.push(values.join(','));
    }
  
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
  } catch (error) {
    console.error('CSV export error:', error);
    throw new Error('Failed to generate CSV: ' + error.message);
  }
};

export const exportToPDF = (data, filename = 'Countries') => {
  try {
    const doc = new jsPDF('landscape');
    
    doc.setFontSize(16);
    doc.text('Countries Report', 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 22);
    
    const tableData = data.map(item => [
      item.id || '',
      item.countryCode || '',
      item.nameAr || '',
      item.nameEng || '',
      item.currencyId || '',
      item.timeZone || '-',
      item.idNationalLength || '',
      item.mobileNumberLength || '',
      item.isActive ? 'Yes' : 'No',
      item.isArabicCountry ? 'Yes' : 'No'
    ]);
    
    autoTable(doc, {
      head: [['ID', 'Code', 'Name (Ar)', 'Name (Eng)', 'Currency', 'Time Zone', 'ID Length', 'Mobile Length', 'Active', 'Arabic']],
      body: tableData,
      startY: 28,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [191, 79, 31], textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { top: 28 },
      theme: 'striped'
    });
    
    doc.save(`${filename}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (error) {
    console.error('PDF export error:', error);
    throw new Error('Failed to generate PDF: ' + error.message);
  }
};

export const downloadFacilitiesAndAmenitiesTemplate = () => {
  const templateData = [
    {
      NameAr: 'موقف سيارات',
      NameEng: 'Parking',
      Icon: '',
      IsActive: true
    },
    {
      NameAr: 'مسبح',
      NameEng: 'Swimming Pool',
      Icon: '',
      IsActive: true
    },
    {
      NameAr: 'صالة ألعاب',
      NameEng: 'Gym',
      Icon: '',
      IsActive: true
    },
    {
      NameAr: 'مصعد',
      NameEng: 'Elevator',
      Icon: '',
      IsActive: true
    },
    {
      NameAr: 'أمن',
      NameEng: 'Security',
      Icon: '',
      IsActive: true
    }
  ];
  
  exportToExcel(templateData, 'FacilitiesAndAmenitiesTemplate');
};

export const downloadBankTemplate = () => {
  const templateData = [
    {
      NameAr: 'البنك الأهلي السعودي',
      NameEng: 'Al Rajhi Bank',
      IsActive: true
    },
    {
      NameAr: 'البنك السعودي الفرنسي',
      NameEng: 'Banque Saudi Fransi',
      IsActive: true
    },
    {
      NameAr: 'بنك الرياض',
      NameEng: 'Riyad Bank',
      IsActive: true
    },
    {
      NameAr: 'البنك السعودي للاستثمار',
      NameEng: 'Saudi Investment Bank',
      IsActive: true
    }
  ];
  
  exportToExcel(templateData, 'BanksTemplate');
};

export const downloadTemplate = (type = 'Country') => {
  if (type === 'RealEstateType') {
    const templateData = [
      {
        NameAr: 'شقة',
        NameEng: 'Apartment',
        IsActive: true
      },
      {
        NameAr: 'فيلا',
        NameEng: 'Villa',
        IsActive: true
      },
      {
        NameAr: 'منزل',
        NameEng: 'House',
        IsActive: true
      },
      {
        NameAr: 'أرض',
        NameEng: 'Land',
        IsActive: true
      },
      {
        NameAr: 'مكتب',
        NameEng: 'Office',
        IsActive: true
      }
    ];
    exportToExcel(templateData, 'RealEstateTypesTemplate');
  } else if (type === 'RealEstateUsage') {
    const templateData = [
      {
        NameAr: 'سكني',
        NameEng: 'Residential',
        IsActive: true
      },
      {
        NameAr: 'تجاري',
        NameEng: 'Commercial',
        IsActive: true
      },
      {
        NameAr: 'صناعي',
        NameEng: 'Industrial',
        IsActive: true
      },
      {
        NameAr: 'زراعي',
        NameEng: 'Agricultural',
        IsActive: true
      },
      {
        NameAr: 'مختلط',
        NameEng: 'Mixed Use',
        IsActive: true
      }
    ];
    exportToExcel(templateData, 'RealEstateUsagesTemplate');
  } else if (type === 'UnitUsage') {
    const templateData = [
      {
        NameAr: 'إيجار',
        NameEng: 'Rent',
        IsActive: true
      },
      {
        NameAr: 'بيع',
        NameEng: 'Sale',
        IsActive: true
      },
      {
        NameAr: 'إيجار طويل الأمد',
        NameEng: 'Long Term Lease',
        IsActive: true
      },
      {
        NameAr: 'إيجار قصير الأمد',
        NameEng: 'Short Term Lease',
        IsActive: true
      },
      {
        NameAr: 'استثمار',
        NameEng: 'Investment',
        IsActive: true
      }
    ];
    exportToExcel(templateData, 'UnitUsagesTemplate');
  } else if (type === 'UnitFacilitiesAndAmenities') {
    const templateData = [
      {
        NameAr: 'مكيف',
        NameEng: 'Air Conditioning',
        IsActive: true
      },
      {
        NameAr: 'مدفأة',
        NameEng: 'Heating',
        IsActive: true
      },
      {
        NameAr: 'شرفة',
        NameEng: 'Balcony',
        IsActive: true
      },
      {
        NameAr: 'مطبخ مجهز',
        NameEng: 'Fitted Kitchen',
        IsActive: true
      },
      {
        NameAr: 'غسالة',
        NameEng: 'Washing Machine',
        IsActive: true
      }
    ];
    exportToExcel(templateData, 'UnitFacilitiesAndAmenitiesTemplate');
  } else if (type === 'AdditionalIncomeReport') {
    const templateData = [
      {
        Date: new Date().toISOString().split('T')[0],
        'Income Description (English)': 'Service Fee',
        'Income Description (Arabic)': 'رسوم الخدمة',
        Amount: 1000.00,
        'Recipient Name (English)': 'John Doe',
        'Recipient Name (Arabic)': 'محمد أحمد',
        Notes: 'Monthly service fee',
        IsActive: true
      },
      {
        Date: new Date().toISOString().split('T')[0],
        'Income Description (English)': 'Consultation Fee',
        'Income Description (Arabic)': 'رسوم الاستشارة',
        Amount: 500.00,
        'Recipient Name (English)': 'Jane Smith',
        'Recipient Name (Arabic)': 'فاطمة علي',
        Notes: 'Consultation services',
        IsActive: true
      }
    ];
    exportToExcel(templateData, 'AdditionalIncomeReportTemplate');
  } else {
    // Default Country template
    const templateData = [
      {
        CountryCode: 'SA',
        NameAr: 'المملكة العربية السعودية',
        NameEng: 'Saudi Arabia',
        CurrencyId: 1,
        TimeZone: 'Asia/Riyadh',
        IdNationalLength: 10,
        MobileNumberLength: 9,
        IsActive: true,
        IsArabicCountry: true
      },
      {
        CountryCode: 'AE',
        NameAr: 'الإمارات العربية المتحدة',
        NameEng: 'United Arab Emirates',
        CurrencyId: 2,
        TimeZone: 'Asia/Dubai',
        IdNationalLength: 15,
        MobileNumberLength: 9,
        IsActive: true,
        IsArabicCountry: true
      },
      {
        CountryCode: 'EG',
        NameAr: 'مصر',
        NameEng: 'Egypt',
        CurrencyId: 3,
        TimeZone: 'Africa/Cairo',
        IdNationalLength: 14,
        MobileNumberLength: 10,
        IsActive: true,
        IsArabicCountry: true
      }
    ];
    exportToExcel(templateData, 'CountriesTemplate');
  }
};

